import React from 'react';
import {
    XMarkIcon,
    DocumentIcon,
    CheckCircleIcon,
    ExclamationCircleIcon,
    ArrowUpTrayIcon,
    StopIcon,
    ArrowPathIcon,
    ClockIcon,
} from '@heroicons/react/24/outline';
import { formatBytes } from '../../utils/formateSize';

export interface UploadTask {
    id: string;
    fileName: string;
    fileSize: number;
    progress: number; // 0-100
    status: 'pending' | 'uploading' | 'completed' | 'failed' | 'paused';
    error?: string;
}

interface UploadProgressPanelProps {
    uploads: UploadTask[];
    onDismiss: (id: string) => void;
    onDismissAll: () => void;
    onRetry?: (id: string) => void;
    onCancel?: (id: string) => void;
}

const UploadProgressPanel: React.FC<UploadProgressPanelProps> = ({
    uploads,
    onDismiss,
    onDismissAll,
    onRetry,
    onCancel,
}) => {
    if (uploads.length === 0) return null;

    const pendingUploads = uploads.filter((u) => u.status === 'pending');
    const activeUploads = uploads.filter((u) => u.status === 'uploading');
    const completedUploads = uploads.filter((u) => u.status === 'completed');
    const failedUploads = uploads.filter((u) => u.status === 'failed');
    const pausedUploads = uploads.filter((u) => u.status === 'paused');

    const inProgressCount = pendingUploads.length + activeUploads.length;
    const allCompleted = inProgressCount === 0 && uploads.length > 0 && failedUploads.length === 0;

    return (
        <div className="fixed bottom-4 right-4 w-96 bg-gray-900 rounded-xl shadow-2xl border border-gray-800 z-50 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700">
                <div className="flex items-center gap-2">
                    <ArrowUpTrayIcon className="w-5 h-5 text-primary-500" />
                    <span className="font-medium text-white">
                        {inProgressCount > 0
                            ? `Uploading ${inProgressCount} file${inProgressCount > 1 ? 's' : ''}`
                            : allCompleted
                                ? `${completedUploads.length} upload${completedUploads.length > 1 ? 's' : ''} complete`
                                : failedUploads.length > 0
                                    ? `${failedUploads.length} upload${failedUploads.length > 1 ? 's' : ''} failed`
                                    : 'Uploads'}
                    </span>
                </div>
                <button
                    onClick={onDismissAll}
                    className="p-1 text-gray-400 hover:text-gray-300 rounded"
                    title="Dismiss all"
                >
                    <XMarkIcon className="w-5 h-5" />
                </button>
            </div>

            {/* Upload List */}
            <div className="max-h-64 overflow-y-auto">
                {uploads.map((upload) => (
                    <div
                        key={upload.id}
                        className="px-4 py-3 border-b border-gray-700 last:border-b-0"
                    >
                        <div className="flex items-start gap-3">
                            {/* Status Icon */}
                            <div className="flex-shrink-0 mt-0.5">
                                {upload.status === 'completed' ? (
                                    <CheckCircleIcon className="w-5 h-5 text-green-400" />
                                ) : upload.status === 'failed' ? (
                                    <ExclamationCircleIcon className="w-5 h-5 text-red-400" />
                                ) : upload.status === 'pending' ? (
                                    <ClockIcon className="w-5 h-5 text-gray-400" />
                                ) : upload.status === 'paused' ? (
                                    <StopIcon className="w-5 h-5 text-yellow-400" />
                                ) : (
                                    <DocumentIcon className="w-5 h-5 text-primary-500 animate-pulse" />
                                )}
                            </div>

                            {/* File Info */}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                    <p className="text-sm font-medium text-white truncate">
                                        {upload.fileName}
                                    </p>
                                    <button
                                        onClick={() => onDismiss(upload.id)}
                                        className="ml-2 p-1 text-gray-400 hover:text-gray-300 rounded flex-shrink-0"
                                    >
                                        <XMarkIcon className="w-4 h-4" />
                                    </button>
                                </div>

                                <p className="text-xs text-gray-400 mt-0.5">
                                    {formatBytes(upload.fileSize)}
                                    {(upload.status === 'uploading' || upload.status === 'pending') && ` • ${upload.progress}%`}
                                    {upload.status === 'pending' && ' • Queued'}
                                    {upload.status === 'paused' && ' • Paused'}
                                </p>

                                {/* Progress Bar */}
                                {(upload.status === 'uploading' || upload.status === 'pending') && (
                                    <div className="mt-2 flex items-center gap-2">
                                        <div className="flex-1 bg-gray-700 rounded-full h-1.5">
                                            <div
                                                className={`h-1.5 rounded-full transition-all duration-300 ${
                                                    upload.status === 'pending' ? 'bg-gray-500' : 'bg-primary-500'
                                                }`}
                                                style={{ width: `${upload.progress}%` }}
                                            />
                                        </div>
                                        {onCancel && (
                                            <button
                                                onClick={() => onCancel(upload.id)}
                                                className="p-0.5 text-gray-400 hover:text-red-400 rounded"
                                                title="Cancel upload"
                                            >
                                                <StopIcon className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* Error Message */}
                                {upload.status === 'failed' && (
                                    <div className="mt-1 flex items-center gap-2">
                                        <p className="text-xs text-red-400 flex-1">
                                            {upload.error || 'Upload failed'}
                                        </p>
                                        {onRetry && (
                                            <button
                                                onClick={() => onRetry(upload.id)}
                                                className="flex items-center gap-1 text-xs text-primary-500 hover:text-primary-400 font-medium"
                                            >
                                                <ArrowPathIcon className="w-3 h-3" />
                                                Retry
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* Success Message */}
                                {upload.status === 'completed' && (
                                    <p className="text-xs text-green-400 mt-0.5">
                                        Uploaded successfully
                                    </p>
                                )}

                                {/* Paused Message */}
                                {upload.status === 'paused' && (
                                    <p className="text-xs text-yellow-400 mt-0.5">
                                        Upload paused
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Summary Footer */}
            {(completedUploads.length > 0 || failedUploads.length > 0 || pausedUploads.length > 0) && inProgressCount > 0 && (
                <div className="px-4 py-2 bg-gray-800 border-t border-gray-700 text-xs text-gray-400">
                    {completedUploads.length > 0 && (
                        <span className="text-green-400">
                            {completedUploads.length} completed
                        </span>
                    )}
                    {completedUploads.length > 0 && (failedUploads.length > 0 || pausedUploads.length > 0) && ' • '}
                    {failedUploads.length > 0 && (
                        <span className="text-red-400">
                            {failedUploads.length} failed
                        </span>
                    )}
                    {failedUploads.length > 0 && pausedUploads.length > 0 && ' • '}
                    {pausedUploads.length > 0 && (
                        <span className="text-yellow-400">
                            {pausedUploads.length} paused
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

export default UploadProgressPanel;
